import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Reconcile gift ledger against actual cart contents
    this.#reconcileGiftLedger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }


  onSubscriptionEnable(data) {
    const { line, sellingPlanId, quantity } = data;
    this.#disableCartItems();

    const { cartTotal } = this.refs;
    
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      selling_plan: sellingPlanId,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }


  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    // Extract variant ID from the item key (format: variant_id:hash)
    const itemKey = cartItemRowToRemove?.dataset.key || '';
    const variantId = itemKey.split(':')[0];

    // If this is a gift parent, strip its discount codes before anything is removed
    // so the checkout button cannot be used with discounts still active during removal.
    const ledger = JSON.parse(localStorage.getItem('giftLedger') || '{}');
    const preStrip =
      variantId && ledger[variantId]?.discountCodes?.length > 0
        ? this.#removeGiftDiscountCodes(ledger[variantId].discountCodes)
        : Promise.resolve();

    // Remove the item after codes are stripped, then cascade gift removal
    preStrip
      .then(() => this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      }))
      .then(() => {
        if (variantId) this.#handleGiftLedgerOnRemoval(variantId);
      });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  // ── Gift Ledger Methods ──────────────────────────────────────────────

  /**
   * Checks the gift ledger when an item is removed and handles cascading.
   * @param {string} variantId - The variant ID of the removed item.
   */
  async #handleGiftLedgerOnRemoval(variantId) {
    const ledger = JSON.parse(localStorage.getItem('giftLedger') || '{}');
    if (Object.keys(ledger).length === 0) return;

    // Case 1: Removed item is a parent — cascade-remove its gifts and discount codes
    if (ledger[variantId]) {
      await this.#cascadeGiftRemoval(variantId, ledger);
      return;
    }

    // Case 2: Removed item is a gift — update the parent's ledger entry
    for (const [parentId, entry] of Object.entries(ledger)) {
      const idx = entry.giftVariantIds.indexOf(variantId);
      if (idx !== -1) {
        entry.giftVariantIds.splice(idx, 1);
        if (entry.giftVariantIds.length === 0) {
          // All gifts gone — remove discount codes and clean up entry
          if (entry.discountCodes?.length > 0) {
            await this.#removeGiftDiscountCodes(entry.discountCodes);
          }
          delete ledger[parentId];
        }
        localStorage.setItem('giftLedger', JSON.stringify(ledger));
        break;
      }
    }
  }

  /**
   * Removes discount codes associated with a parent variant and cleans up the ledger.
   * Gift items themselves are left in the cart.
   * @param {string} parentVariantId - The parent's variant ID.
   * @param {Object} ledger - The current gift ledger object.
   */
  async #cascadeGiftRemoval(parentVariantId, ledger) {
    const entry = ledger[parentVariantId];
    if (!entry) return;

    try {
      // Remove associated discount codes
      if (entry.discountCodes?.length > 0) {
        await this.#removeGiftDiscountCodes(entry.discountCodes);
      }

      // Clean up ledger entry
      delete ledger[parentVariantId];
      localStorage.setItem('giftLedger', JSON.stringify(ledger));
    } catch (error) {
      console.error('[cascadeGiftRemoval] Error:', error);
    }
  }

  /**
   * Removes specific discount codes from the cart.
   * @param {string[]} codesToRemove - Discount codes to strip.
   */
  async #removeGiftDiscountCodes(codesToRemove) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const currentCodes = (cart.discount_codes || []).map(
        /** @param {{ code: string }} d */ (d) => d.code
      );

      const remaining = currentCodes.filter(
        /** @param {string} code */ (code) => !codesToRemove.includes(code)
      );

      await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
        body: JSON.stringify({ discount: remaining.join(',') || '' }),
      }));
    } catch (error) {
      console.error('[removeGiftDiscountCodes] Error:', error);
    }
  }

  /**
   * On page load, reconciles the localStorage gift ledger against the actual cart.
   * Removes orphaned gifts whose parent is no longer in the cart.
   */
  async #reconcileGiftLedger() {
    const ledger = JSON.parse(localStorage.getItem('giftLedger') || '{}');
    if (Object.keys(ledger).length === 0) return;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const cartVariantIds = new Set(cart.items.map(/** @param {any} item */ (item) => String(item.variant_id)));

      let changed = false;

      for (const [parentId, entry] of Object.entries(ledger)) {
        if (!cartVariantIds.has(parentId)) {
          // Parent is gone — remove its gifts from the cart
          const giftItems = cart.items.filter(
            /** @param {any} item */
            (item) =>
              entry.giftVariantIds.includes(String(item.variant_id)) &&
              item.properties?._gift_parent === parentId
          );

          for (const giftItem of giftItems) {
            await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
              body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
            }));
          }

          if (entry.discountCodes?.length > 0) {
            await this.#removeGiftDiscountCodes(entry.discountCodes);
          }

          delete ledger[parentId];
          changed = true;
        }
      }

      if (changed) {
        localStorage.setItem('giftLedger', JSON.stringify(ledger));
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    } catch (error) {
      console.error('[reconcileGiftLedger] Error:', error);
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
